import React, { useCallback, useEffect, useRef } from 'react';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import startMarkerPicto from '../../assets/SVGIcons/Start-Marker.svg';
import viaMarkerPicto from '../../assets/SVGIcons/Via-Marker.svg';
import endMarkerPicto from '../../assets/SVGIcons/End-Marker.svg';
import {
    adjustZoomLevel,
    calculateDistance,
    calculateDistanceList,
    calculateZoomLevel,
    createRoutingParameter,
    TourWaypointEntry,
} from '../../utils/hereMapUtils';
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { setDetailedDistanceList, setMapCaptureFinished, setTourDistances } from "../../store/Vanlifer/tourSlice";

declare const H: any;

function buildColoredMarkerSvg(fillColor: string, strokeColor?: string): string {
    return `data:image/svg+xml;utf8,${encodeURIComponent(
        `<svg viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg"><path fill="${fillColor}" ${strokeColor ? `stroke="${strokeColor}" stroke-width="24" paint-order="stroke fill markers"` : ""} d="m172.268 501.67c-145.298-210.639-172.268-232.257-172.268-309.67 0-106.039 85.961-192 192-192s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zm19.732-229.67c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"/></svg>`
    )}`;
}

interface HereMapProps {
    apiKey: string;
    waypoints: string[];
    waypointEntries?: TourWaypointEntry[];
    height: string;
    editorSite: boolean;
    tourId: number;
    highlightedWaypointIndex?: number | null;
    hoveredWaypointIndex?: number | null;
    onHoveredWaypointIndexChange?: (index: number | null) => void;
    onExpand?: () => void;
    showExpandButton?: boolean;
    showWaypointLabels?: boolean;
}

const HereMapTourComponent: React.FC<HereMapProps> = ({
                                                          apiKey,
                                                          waypoints,
                                                          waypointEntries,
                                                            height,
                                                            editorSite,
                                                            tourId,
                                                            highlightedWaypointIndex = null,
                                                            hoveredWaypointIndex = null,
                                                            onHoveredWaypointIndexChange,
                                                            onExpand,
                                                            showExpandButton = false,
                                                            showWaypointLabels = false,
                                                        }) => {
    const theme = useTheme();
    const mapRef = useRef<HTMLDivElement>(null);
    const staticMapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<H.Map | null>(null);
    const uiInstance = useRef<H.ui.UI | null>(null);
    const routeObjectsRef = useRef<any[]>([]);
    const markerObjectsRef = useRef<any[]>([]);
    const markerLabelObjectsRef = useRef<any[]>([]);
    const disposedRef = useRef(false);
    const requestIdRef = useRef(0);
    const captureInProgressRef = useRef(false);
    const dispatch = useAppDispatch();
    const showStaticMap = useAppSelector(state => state.tour.showStaticMap);
    const mapCaptureFinished = useAppSelector(state => state.tour.mapCaptureFinished);
    const activeAccentColor = theme.palette.mode === 'light' ? theme.palette.secondary.main : theme.palette.primary.main;
    const markerOutlineColor = theme.palette.common.white;

    const alignMapControls = useCallback(() => {
        const mapElement = mapRef.current;
        if (!mapElement) {
            return;
        }

        const zoomUiControl = uiInstance.current?.getControl('zoom');
        if (zoomUiControl?.setAlignment) {
            zoomUiControl.setAlignment('left-middle' as any);
        }

        const zoomControl = mapElement.querySelector('.H_zoom') as HTMLElement | null;
        if (zoomControl) {
            zoomControl.style.top = '68px';
            zoomControl.style.left = '-4px';
            zoomControl.style.right = 'auto';
        }

        const zoomControlWrappers = mapElement.querySelectorAll('.H_zoom, .H_ctl_zoom, [class*="zoom"]');
        zoomControlWrappers.forEach((element) => {
            const htmlElement = element as HTMLElement;
            htmlElement.style.left = '-4px';
            htmlElement.style.top = '68px';
            htmlElement.style.right = 'auto';
        });
    }, []);

    const clearMapObjects = useCallback((objectsRef: React.MutableRefObject<any[]>) => {
        if (!mapInstance.current || objectsRef.current.length === 0) {
            return;
        }

        mapInstance.current.removeObjects(objectsRef.current);
        objectsRef.current = [];
    }, []);

    const createWaypointLabelMarkers = useCallback((entries: TourWaypointEntry[]) => {
        if (typeof H?.map?.DomMarker === 'undefined') {
            return [];
        }

        return entries.map((entry) => {
            const [lat, lng] = entry.coordinate.split(',').map(Number);
            const labelNode = document.createElement('div');
            labelNode.style.position = 'relative';
            labelNode.style.transform = 'translate(14px, -40px)';
            labelNode.style.pointerEvents = 'none';
            labelNode.innerHTML = `
                <div style="display:inline-flex;flex-direction:column;gap:2px;padding:8px 10px;border-radius:10px;background:rgba(255,255,255,0.82);backdrop-filter:blur(8px);box-shadow:0 8px 20px rgba(15,23,42,0.14);border:1px solid rgba(15,23,42,0.08);min-width:110px;max-width:180px;">
                    <div style="font:600 10px/1.2 system-ui, sans-serif;letter-spacing:0.05em;text-transform:uppercase;color:rgba(15,23,42,0.62);">${entry.typeLabel}</div>
                    <div style="font:700 12px/1.25 system-ui, sans-serif;color:rgba(15,23,42,0.92);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${entry.title}</div>
                </div>
            `;

            return new H.map.DomMarker({ lat, lng }, { icon: new H.map.DomIcon(labelNode) });
        });
    }, []);

    const capture = useCallback((map: H.Map, ui: H.ui.UI | null) => {
        if (disposedRef.current || captureInProgressRef.current) {
            return;
        }

        captureInProgressRef.current = true;

        map.capture((canvas: HTMLCanvasElement | null) => {
            captureInProgressRef.current = false;
            if (!staticMapRef.current || disposedRef.current) return;
            staticMapRef.current.innerHTML = '';
            if (canvas) staticMapRef.current.appendChild(canvas);
            else staticMapRef.current.innerText = 'Capturing is not supported';
            dispatch(setMapCaptureFinished(true));
        }, ui ? [ui] : undefined);
    }, [dispatch]);

    const createWaypointMarkers = useCallback((waypointsToRender: string[]) => {
        const defaultIconSize = new H.math.Size(18, 24);
        const hoveredIconSize = new H.math.Size(22, 29);
        const startIcon = new H.map.Icon(startMarkerPicto, { size: defaultIconSize });
        const endIcon = new H.map.Icon(endMarkerPicto, { size: defaultIconSize });
        const viaIcon = new H.map.Icon(viaMarkerPicto, { size: defaultIconSize });
        const highlightedIcon = new H.map.Icon(buildColoredMarkerSvg(activeAccentColor, markerOutlineColor), { size: defaultIconSize });
        const hoveredIcon = new H.map.Icon(buildColoredMarkerSvg(activeAccentColor, theme.palette.common.black), { size: hoveredIconSize });

        return waypointsToRender.map((waypoint, index) => {
            const [lat, lng] = waypoint.split(',').map(Number);
            const isFirst = index === 0;
            const isLast = index === waypointsToRender.length - 1;
            const isHighlighted = highlightedWaypointIndex === index;
            const isHovered = hoveredWaypointIndex === index;

            let icon = viaIcon;
            if (isHighlighted) {
                icon = highlightedIcon;
            } else if (isHovered) {
                icon = hoveredIcon;
            } else if (isFirst) {
                icon = startIcon;
            } else if (isLast) {
                icon = endIcon;
            }

            return new H.map.Marker({ lat, lng }, {
                data: {
                    label: isHighlighted ? 'Aktiver Aufenthalt' : isHovered ? 'Ausgewählter Aufenthalt' : isFirst ? 'Start' : isLast ? 'Ziel' : 'Zwischenstopp',
                },
                icon,
            });
        }).map((marker, index) => {
            marker.addEventListener('pointerenter', () => onHoveredWaypointIndexChange?.(index));
            marker.addEventListener('pointerleave', () => onHoveredWaypointIndexChange?.(null));
            return marker;
        });
    }, [activeAccentColor, highlightedWaypointIndex, hoveredWaypointIndex, markerOutlineColor, onHoveredWaypointIndexChange, theme.palette.common.black]);

    const renderWaypointMarkers = useCallback((waypointsToRender: string[]) => {
        clearMapObjects(markerObjectsRef);
        clearMapObjects(markerLabelObjectsRef);

        if (!mapInstance.current || waypointsToRender.length === 0) {
            return;
        }

        const markers = createWaypointMarkers(waypointsToRender);
        mapInstance.current.addObjects(markers);
        markerObjectsRef.current = markers;

        if (showWaypointLabels && waypointEntries?.length) {
            const labelMarkers = createWaypointLabelMarkers(waypointEntries);
            mapInstance.current.addObjects(labelMarkers);
            markerLabelObjectsRef.current = labelMarkers;
        }
    }, [clearMapObjects, createWaypointLabelMarkers, createWaypointMarkers, showWaypointLabels, waypointEntries]);

    useEffect(() => {
        if (showStaticMap && mapInstance.current) {
            window.requestAnimationFrame(() => {
                if (!disposedRef.current && mapInstance.current) {
                    capture(mapInstance.current, uiInstance.current);
                }
            });
        }
    }, [showStaticMap, capture, mapCaptureFinished, waypoints.length]);

    useEffect(() => {
        if (!showStaticMap && staticMapRef.current) {
            staticMapRef.current.innerHTML = '';
        }
    }, [showStaticMap]);

    useEffect(() => {
        if (!waypoints.length || typeof H === 'undefined') return;

        disposedRef.current = false;
        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;
        let cancelled = false;

        const frameId = window.requestAnimationFrame(() => {
            if (cancelled || disposedRef.current || currentRequestId !== requestIdRef.current) {
                return;
            }

            const platform = new H.service.Platform({ apikey: apiKey });
            const defaultLayers = platform.createDefaultLayers() as H.service.DefaultLayers;

            const [zoomLevel, centerLat, centerLng] = calculateZoomLevel(waypoints);
            const center = { lat: centerLat, lng: centerLng };
            const zoom = adjustZoomLevel(zoomLevel, editorSite);

            if (!mapRef.current) {
                return;
            }

            const map = mapInstance.current ?? new H.Map(
                mapRef.current,
                defaultLayers.vector.normal.map,
                { pixelRatio: window.devicePixelRatio, center, zoom }
            );
            mapInstance.current = map;

            if (editorSite && !uiInstance.current) {
                new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
                uiInstance.current = H.ui.UI.createDefault(map, defaultLayers, 'de-DE');
                if (uiInstance.current) {
                    const mapSettingsControl = uiInstance.current.getControl('mapsettings');
                    if (mapSettingsControl) {
                        uiInstance.current.removeControl('mapsettings');
                    }
                }
                window.requestAnimationFrame(() => {
                    alignMapControls();
                });
            }

            clearMapObjects(routeObjectsRef);
            clearMapObjects(markerObjectsRef);
            clearMapObjects(markerLabelObjectsRef);
            map.setCenter(center);
            map.setZoom(zoom);

            if (waypoints.length === 1) {
                renderWaypointMarkers(waypoints);
                map.setZoom(14);
                return;
            }

            const router = platform.getRoutingService(undefined, 8);
            const routingParams = createRoutingParameter(waypoints);

            router.calculateRoute(
                routingParams,
                (result: any) => {
                    if (disposedRef.current || currentRequestId !== requestIdRef.current || !mapInstance.current) {
                        return;
                    }

                    const routes = result.routes;
                    if (!result || !result.routes || result.routes.length === 0) {
                        console.warn("Keine gültige Route erhalten.");
                        return;
                    }
                    const firstRoute = routes[0];

                    if (editorSite) {
                        const list = calculateDistanceList(firstRoute);
                        dispatch(setDetailedDistanceList({ detailedDistanceList: list }));
                    }

                    const totalDistance = calculateDistance(firstRoute);
                    dispatch(setTourDistances({ tourId, distance: totalDistance }));

                    firstRoute.sections.forEach((section: any, i: number) => {
                        if (disposedRef.current || currentRequestId !== requestIdRef.current || !mapInstance.current) {
                            return;
                        }

                        const line = H.geo.LineString.fromFlexiblePolyline(section.polyline);

                        const routeOutline = new H.map.Polyline(line, {
                            data: { type: 'route', i },
                            style: {
                                lineWidth: 7,
                                strokeColor: '#1B2934',
                                lineTailCap: 'arrow-tail',
                                lineHeadCap: 'arrow-head'
                            }
                        });

                        const routeArrows = new H.map.Polyline(line, {
                            data: { type: 'arrows', i },
                            style: {
                                lineWidth: 7,
                                strokeColor: 'white',
                                lineDash: [0, 3],
                                lineTailCap: 'arrow-tail',
                                lineHeadCap: 'arrow-head'
                            }
                        });

                        map.addObjects([routeOutline, routeArrows]);
                        routeObjectsRef.current.push(routeOutline, routeArrows);
                    });

                    if (disposedRef.current || currentRequestId !== requestIdRef.current || !mapInstance.current) {
                        return;
                    }

                    renderWaypointMarkers(waypoints);
                },
                (error: Error) => {
                    if (disposedRef.current || currentRequestId !== requestIdRef.current) {
                        return;
                    }
                    console.error('HERE Routing Fehler:', error);
                    if (error.name === 'InvalidRequestError') {
                        alert('Ungültige Routing-Anfrage. Bitte überprüfen Sie die Koordinaten.');
                    } else if (error.name === 'QuotaExceededError' || error.message.includes('429')) {
                        alert('API-Limit erreicht. Bitte später erneut versuchen.');
                    } else {
                        alert(`Unbekannter Fehler beim Routing: ${error.message || error}`);
                    }
                }
            );
        });

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(frameId);
        };
    }, [apiKey, waypoints, editorSite, tourId, dispatch, clearMapObjects, renderWaypointMarkers, alignMapControls]);

    useEffect(() => {
        if (!mapInstance.current || waypoints.length === 0) {
            return;
        }

        renderWaypointMarkers(waypoints);
    }, [highlightedWaypointIndex, renderWaypointMarkers, waypoints]);

    useEffect(() => {
        return () => {
            disposedRef.current = true;
            requestIdRef.current += 1;
            captureInProgressRef.current = false;
            clearMapObjects(routeObjectsRef);
            clearMapObjects(markerObjectsRef);
            clearMapObjects(markerLabelObjectsRef);
            if (mapInstance.current) {
                mapInstance.current.dispose();
                mapInstance.current = null;
            }
            uiInstance.current = null;
        };
    }, [clearMapObjects]);

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {showExpandButton && onExpand && (
                <Tooltip title="Karte vergrössern">
                    <IconButton
                        size="small"
                        onClick={onExpand}
                        sx={{
                            position: 'absolute',
                            top: 10,
                            left: 24,
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 1px 4px rgba(15, 23, 42, 0.18)',
                            width: 36,
                            height: 36,
                            '&:hover': { bgcolor: 'background.paper' },
                        }}
                    >
                        <OpenInFullRoundedIcon fontSize="medium" />
                    </IconButton>
                </Tooltip>
            )}
            <div ref={staticMapRef} style={{
                width: '100%',
                height,
                borderRadius: '10px',
                visibility: showStaticMap && mapCaptureFinished ? 'visible' : 'hidden',
                position: showStaticMap && mapCaptureFinished ? 'relative' : 'absolute',
            }} />
            <div ref={mapRef} style={{
                width: '100%',
                height,
                borderRadius: '10px',
                visibility: showStaticMap && mapCaptureFinished ? 'hidden' : 'visible',
                position: showStaticMap && mapCaptureFinished ? 'absolute' : 'relative'
            }} />
        </Box>
    );
};

export default HereMapTourComponent;
